"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cn } from "@bednbody/ui/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";

const surfaceCardVariants = cva("rounded-card bg-card p-4 shadow-card-quiet", {
  variants: {
    tone: {
      default: "",
      gold: "bg-linear-to-br from-gold-light to-gold-light/20",
      secondary: "bg-secondary border border-primary/10",
    },
    interactive: {
      true: "transition-shadow duration-200 hover:ring-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      false: "",
    },
  },
  compoundVariants: [
    { tone: "default", interactive: true, class: "hover:ring-primary/20" },
    { tone: "gold", interactive: true, class: "hover:ring-gold/30" },
    { tone: "secondary", interactive: true, class: "hover:ring-primary/20" },
  ],
  defaultVariants: { tone: "default", interactive: false },
});

export type SurfaceCardProps = useRender.ComponentProps<"div"> &
  VariantProps<typeof surfaceCardVariants>;

export function SurfaceCard({
  className,
  render,
  tone = "default",
  interactive = false,
  ...props
}: SurfaceCardProps) {
  return useRender({
    defaultTagName: "div",
    render,
    props: mergeProps<"div">(
      {
        className: cn(surfaceCardVariants({ tone, interactive }), className),
      } as React.ComponentProps<"div">,
      props as React.ComponentProps<"div">,
    ),
  });
}
SurfaceCard.displayName = "SurfaceCard";

export { surfaceCardVariants };
